import { proxy } from 'valtio';

import {
    FeatureFlags,
    FeatureKey,
    PermissionDiagram,
    PermissionDiagramElements,
    PermissionDiagramFiles,
    PermissionDiagramGeneral,
    PermissionDiagramGroups,
    PermissionDiagramImages,
    PermissionDiagramSimulations,
    PermissionDigramComments,
    PermissionProject,
    Project,
    subscriptionCanUseFeature,
} from 'models';
import { FeatureFlagHelpers } from 'helpers/FeatureFlagHelpers';

import { state as userState } from 'state/current-user';

type Permissions = Partial<Record<PermissionDiagram, boolean>>;

export const permissionsState = proxy<{ permissions: Permissions }>({
    permissions: {},
});

export const setPermissions = (project: Project | null) => {
    if (!project) {
        clearPermissions();
    } else {
        setProjectPermissions(project);
    }
};

export function clearPermissions(): void {
    permissionsState.permissions = {};
}

function setProjectPermissions(project: Project) {
    const { permissions: projectPermissions, permissionsAs, teamDesignerSubscription } = project;

    // reset state before setting individual permissions
    permissionsState.permissions = {};

    if (
        projectPermissions.includes(PermissionProject.ALL) ||
        projectPermissions.includes(PermissionProject.ADMIN) ||
        projectPermissions.includes(PermissionProject.VIEW)
    ) {
        permissionsState.permissions[PermissionDiagramElements.VIEW] = true;
        permissionsState.permissions[PermissionDiagramImages.VIEW] = true;
        permissionsState.permissions[PermissionDiagramGroups.VIEW] = true;

        permissionsState.permissions[PermissionDiagramGeneral.BOM] = true;
    }

    if (projectPermissions.includes(PermissionProject.ALL) || projectPermissions.includes(PermissionProject.ADMIN)) {
        permissionsState.permissions[PermissionDiagramGeneral.SHARE] = true;
    }

    if (
        projectPermissions.includes(PermissionProject.ALL) ||
        projectPermissions.includes(PermissionProject.ADMIN) ||
        projectPermissions.includes(PermissionProject.EDIT)
    ) {
        permissionsState.permissions[PermissionDiagramGeneral.EDIT] = true;
        permissionsState.permissions[PermissionDiagramGeneral.DOWNLOAD] = true;

        if (permissionsAs === 'user') {
            permissionsState.permissions[PermissionDiagramGeneral.REQUEST_SUPPORT] = true;
            permissionsState.permissions[PermissionDiagramGeneral.CREDITS] = true;
        }

        if (permissionsAs === 'user' || permissionsAs === 'collaborator') {
            permissionsState.permissions[PermissionDiagramGeneral.AI] = true;
        }

        permissionsState.permissions[PermissionDiagramElements.CREATE] = true;
        permissionsState.permissions[PermissionDiagramElements.EDIT] = true;

        permissionsState.permissions[PermissionDiagramImages.CREATE] = true;
        permissionsState.permissions[PermissionDiagramImages.EDIT] = true;

        permissionsState.permissions[PermissionDiagramGroups.CREATE] = true;
        permissionsState.permissions[PermissionDiagramGroups.EDIT] = true;

        permissionsState.permissions[PermissionDiagramFiles.CREATE] = true;
        permissionsState.permissions[PermissionDiagramFiles.EDIT] = true;
        permissionsState.permissions[PermissionDiagramFiles.VIEW] = true;

        permissionsState.permissions[PermissionDiagramSimulations.VIEW] = true;

        const userHasSimulation = FeatureFlagHelpers.isEnabledForProject(FeatureFlags.SIMULATION, {
            user: userState.user,
            project,
        });
        const hasProjectSimulatePermission =
            (permissionsAs === 'user' || permissionsAs === 'collaborator') &&
            subscriptionCanUseFeature(teamDesignerSubscription, FeatureKey.RUN_SIMULATIONS);

        if (userHasSimulation || hasProjectSimulatePermission) {
            permissionsState.permissions[PermissionDiagramSimulations.CREATE] = true;
            permissionsState.permissions[PermissionDiagramSimulations.EDIT] = true;
        }
    }

    if (permissionsAs === 'company') {
        permissionsState.permissions[PermissionDiagramSimulations.VIEW] = true;
    }

    if (projectPermissions.includes(PermissionProject.COMMENT)) {
        permissionsState.permissions[PermissionDigramComments.VIEW] = true;
        permissionsState.permissions[PermissionDigramComments.CREATE] = true;
        permissionsState.permissions[PermissionDigramComments.EDIT] = true;
    }

    if (projectPermissions.includes(PermissionProject.COMPANY)) {
        permissionsState.permissions[PermissionDiagramGeneral.COMPANY] = true;
    }
}
